import { Dialog } from "@headlessui/react";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import {
  CategoryStandardProduct,
  Product,
} from "shared/api/middleware-api-client";
import Modal, { ModalType } from "shared/ui/modal";
import Heading from "shared/ui/typography/Heading";
import MonthlyPayment from "widgets/shared/monthly-payment";
import { DoorModal } from "./components/DoorModal";
import { BusinessDoorModal } from "./components/BusinessDoorModal";
import { zakelijkDoorCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";

export enum ProductModalType {
  DOOR,
  STANDARD_PRODUCT,
}

export interface ProductModalProps {
  isOpen: boolean;
  totalPrice: string;
  monthlyPayment?: number;
  setIsOpen: (value: boolean) => void;
  type: ProductModalType;
  product: CategoryStandardProduct | Product;
}

function isProduct(product: any): product is Product {
  return (product as Product).doorType !== undefined;
}

const ProductModal = ({
  isOpen,
  totalPrice,
  monthlyPayment,
  setIsOpen,
  product,
}: ProductModalProps) => {
  const { extra, selfAssembleDoorData, selectedDoor } =
    useConfigurationOptionsContext();
  const isSelfAssembly = selfAssembleDoorData.selfAssembly;
  const showMonthlyPayment = (monthlyPayment ?? 0) > 0;

  if (!selectedDoor) return null;
  const isBusinessDoor = zakelijkDoorCodes.includes(selectedDoor?.code);

  const showSelfAssemblyTexts = isSelfAssembly && !isBusinessDoor;
  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      type={ModalType.SQUARE}
      hideHeader={true}
      hasProductFooter={true}
      className="gg-relative gg-z-[1000] gg-overflow-visible"
    >
      <Dialog.Panel>
        <div className="gg-relative gg-m-auto gg-w-1/2 gg-pt-4"></div>

        <Heading
          data-testid="modalHeading"
          headingLevel="h2"
          className="gg-leading-[1.5rem]"
        >
          Jouw configuratie is aan je winkelmand toegevoegd!
        </Heading>
        <section className="gg-mt-2 gg-py-5">
          <Heading headingLevel="h3" className="gg-pb-3.5">
            {isProduct(product) ? product.doorType.name : "Jouw keuze"}
          </Heading>
          {isBusinessDoor ? (
            <BusinessDoorModal product={product as Product} extra={extra} />
          ) : (
            <DoorModal
              product={product as Product}
              isSelfAssembly={isSelfAssembly}
            />
          )}
        </section>
        <section className="gg-flex gg-justify-between gg-pb-5">
          <div>
            <Heading headingLevel="h4">Totaal</Heading>
            <p className="gg-inline-flex gg-items-center gg-text-sm gg-font-light gg-text-gray-500">
              Incl. BTW
            </p>
          </div>
          <div className="gg-text-end">
            <Heading headingLevel="h4">{totalPrice}</Heading>
            {showSelfAssemblyTexts && (
              <p className="gg-w-full gg-text-xs">zelfmontage prijs</p>
            )}
            {showMonthlyPayment && (
              <MonthlyPayment
                className="gg-w-full"
                showInfoButton={false}
                monthlyPayment={monthlyPayment}
              />
            )}
          </div>
        </section>
      </Dialog.Panel>
    </Modal>
  );
};

export default ProductModal;
